import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useDemoStore } from '../store';

type Props = { children: ReactNode };
type State = { caught: Error | null };

/**
 * React error boundary for the live VoiceDemo page (Bug 1 fix — round-8-voice).
 *
 * Problem: an unhandled render error left the page showing a blank/crashed screen while the
 * voice session state (agent monologuing, governance overlay) was still live in the store.
 *
 * Fix: catch the error at the VoiceDemo boundary, reset voice session state (so the zombie
 * store state doesn't leak), and render a recovery affordance ("Start over") rather than a
 * dead screen. Booth staff can tap "Start over" to resume between visitors without a page
 * reload; the normal new-session flow (TalkButton → connect) begins immediately.
 */
export class LivePageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { caught: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { caught: error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Reset the voice session store so the zombie governance overlay and WS state are cleared.
    useDemoStore.getState().reset();
    console.error('[LivePageErrorBoundary] render error:', error, info.componentStack);
  }

  handleRestart = () => {
    // A second reset for safety (in case reset wasn't called in componentDidCatch yet).
    useDemoStore.getState().reset();
    this.setState({ caught: null });
  };

  override render() {
    if (this.state.caught) {
      return (
        <div
          style={{
            height: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1.5rem',
            padding: '2rem',
            background: 'var(--c-canvas, #1a1614)',
            color: 'var(--c-ink, #f0ece6)',
            fontFamily: 'var(--font-body, system-ui)',
            textAlign: 'center',
          }}
        >
          <p
            style={{
              fontSize: '1rem',
              color: 'var(--c-subtle, #a09688)',
              maxWidth: '28ch',
              lineHeight: 1.5,
            }}
          >
            The concierge ran into a problem and had to stop.
          </p>
          <button
            type="button"
            onClick={this.handleRestart}
            style={{
              padding: '0.75rem 2.5rem',
              borderRadius: '9999px',
              background: 'var(--c-walnut, #5c3d2e)',
              color: 'var(--c-on-accent, #f0ece6)',
              border: 'none',
              fontFamily: 'var(--font-display, serif)',
              fontSize: '1rem',
              cursor: 'pointer',
              letterSpacing: '0.02em',
            }}
          >
            Start over
          </button>
          {import.meta.env.DEV && (
            <pre
              style={{
                fontSize: '0.7rem',
                color: 'var(--c-terracotta, #b5533c)',
                maxWidth: '60ch',
                textAlign: 'left',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
              }}
            >
              {this.state.caught.message}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
